"use client";

import { createContext, useContext, useMemo, type ReactNode } from "react";
import { DEFAULT_LOCALE } from "@/lib/data/languages";
import { getTranslations, type TranslationStrings } from "@/lib/i18n/translations";
import { useLocalStorageState } from "@/lib/hooks/useLocalStorageState";

interface LocaleContextValue {
  locale: string;
  setLocale: (code: string) => void;
  /** False until localStorage has been read — gate locale-sensitive fetches on this. */
  hydrated: boolean;
  t: TranslationStrings;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocale, hydrated] = useLocalStorageState<string>(
    "nursetools_locale",
    DEFAULT_LOCALE,
  );

  const value = useMemo<LocaleContextValue>(
    () => ({ locale, setLocale, hydrated, t: getTranslations(locale) }),
    [locale, setLocale, hydrated],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useLocale must be used within <LocaleProvider>");
  return ctx;
}
